/**
 * Seeking a stored session, without simulating it.
 *
 * A log is a delta stream, so "show me tick 4,000" has no answer on its own —
 * the state at 4,000 is every event before it, folded. Replaying 4,000 ticks of
 * events to move a slider would be seconds of work per drag.
 *
 * `KEYFRAME` is what makes it cheap. The server emits a full `SerialisedState`
 * every `KEYFRAME_INTERVAL_TICKS` (two seconds) and on join, so the state at
 * any tick is the last keyframe at or before it, plus at most two seconds of
 * events. That is the entire reason keyframes are in the stream at all
 *, and this file is where the promise is collected.
 *
 * Everything here is a pure function of the frames, so the seek logic is tested
 * without a canvas, a socket or a clock.
 */

import type { EventFrame } from '@overcooked/shared'

export interface ReplayIndex {
  /** Frames in tick order — the log's own order, but not trusted to be sorted. */
  frames: EventFrame[]
  /** Indices into `frames` of every frame that carries a KEYFRAME, ascending. */
  keyframes: number[]
  firstTick: number
  lastTick: number
}

export function indexFrames(frames: EventFrame[]): ReplayIndex {
  const ordered = [...frames].sort((a, b) => a.tick - b.tick)
  const keyframes: number[] = []
  for (let i = 0; i < ordered.length; i += 1) {
    if (ordered[i].events.some((event) => event.type === 'KEYFRAME')) keyframes.push(i)
  }
  return {
    frames: ordered,
    keyframes,
    firstTick: ordered.length > 0 ? ordered[0].tick : 0,
    lastTick: ordered.length > 0 ? ordered[ordered.length - 1].tick : 0,
  }
}

/**
 * The last keyframe at or before `tick`, as an index into `index.frames`.
 *
 * Returns 0 when the target is before the first keyframe — which happens only
 * for a log that begins mid-round, since a round's own tick 0 carries one.
 */
export function keyframeAtOrBefore(index: ReplayIndex, tick: number): number {
  let best = -1
  let lo = 0
  let hi = index.keyframes.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (index.frames[index.keyframes[mid]].tick <= tick) {
      best = index.keyframes[mid]
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return best === -1 ? 0 : best
}

export interface Seek {
  /** Frames to push, in order, to arrive at `tick` from a reset feed. */
  frames: EventFrame[]
  /** Index into `index.frames` of the first frame *after* the seek. */
  cursor: number
  /** The keyframe the seek started from — how far back the fold had to go. */
  fromTick: number
}

/**
 * Everything needed to show tick `tick`: one keyframe and the events since.
 *
 * The returned frames are pushed into a freshly reset `EventFeed`, which is
 * exactly what a client joining mid-round does with the keyframe the server
 * sends it. Replay and a late join are the same code path.
 */
export function seekTo(index: ReplayIndex, tick: number): Seek {
  const start = keyframeAtOrBefore(index, tick)
  let cursor = start
  while (cursor < index.frames.length && index.frames[cursor].tick <= tick) cursor += 1
  return {
    frames: index.frames.slice(start, Math.max(cursor, start + 1)),
    cursor,
    fromTick: index.frames.length > 0 ? index.frames[start].tick : 0,
  }
}
